import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * Area `customer-order-payment` of the console gateway — plan-005 **OP-36** (design §18.6.8 IC-56,
 * §18.15.1 IC-52a, §18.14.3 DD5-33; FR-PAY-10, FR-PAY-11, FR-PAY-12).
 *
 * An order's online payment, as the Orders tab, the Order detail and the voice agent's Orders tab
 * show it and act on it:
 *
 *   · **the display state** — the eight states of `customerOrderPaymentStateOf` (IC-56). The API
 *     derives it (`payment_state` on every row, `payment.state` on the detail); this file only
 *     names, colours and guards it. The three guards — {@link customerOrderAwaitsPayment} (OD-P6
 *     (a)), {@link startingWarnsUnpaidOnline} and {@link markPaidIsAvailable} — restate
 *     `packages/order-book/src/payment-state.ts` for the browser, word for word, because the
 *     console does not bundle a package that carries a Postgres store. `customer-order-payment.test.ts`
 *     pins every state of each, so a change there that is not made here fails a test here.
 *   · **the detail's `payment` block** — expiry, paid amount and time, refunds, the SMS line and the
 *     payment timeline, decoded from integer minor units (ADR 0014; no float crosses here).
 *   · **the five acts** (IC-52a): resend the link, cancel the link, refund (Owner/Admin, OD-P5),
 *     switch to hand-over (`setCustomerOrderPaymentMethod`), and the per-shop stream the screens
 *     re-read on (DD5-33: every payment change publishes the existing `customer_order` event).
 *
 * ── BUILT AGAINST THE CONTRACT, BEFORE THE ROUTES ─────────────────────────────────────────
 * OP-35 adds the four routes in parallel with this unit, so the generated client does not carry
 * them yet. The slice therefore reaches each through {@link documentedOperationOf}: the operation
 * must be in `API_OPERATION` (the assembled spec) AND be a method of the generated client, or the
 * call raises {@link UndocumentedOperationError} naming the operationId and OP-35. There is no path
 * string here and no use of the client's protected `send`: the rule "the console cannot reach an
 * endpoint the spec does not carry" holds exactly as for every other slice. Once OP-35 is merged
 * and `pnpm api:build-spec` has run, the four resolve with no edit here; the integrator may then
 * replace the look-up with direct calls.
 *
 * The register is EMPTY: nothing here is refused by design, only until the spec carries it.
 */
import { API_OPERATION, assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import {
  CUSTOMER_ORDER_PAYMENT_EVENT_KIND,
  PROVIDER_MODE,
  SMS_MESSAGE_STATE,
  money,
  type CurrencyCode,
  type CustomerOrderPaymentEventKind,
  type MemberRole,
  type Money,
  type ProviderMode,
  type SmsMessageState,
} from "@wetalk/domain";

import type { ConsoleGateway, StreamListener, StreamSubscription, WorkspaceRoleView } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { UndocumentedOperationError, type GatewayGap } from "./slice-context.js";

// ---------------------------------------------------------------------------------------------
// The display state (IC-56), restated for the browser
// ---------------------------------------------------------------------------------------------

/** `CUSTOMER_ORDER_PAYMENT_DISPLAY_STATE` of `@wetalk/order-book`, in the same order. */
export const CUSTOMER_ORDER_PAYMENT_DISPLAY_STATE = [
  "not_online",
  "awaiting_online_payment",
  "online_payment_not_completed",
  "paid_online",
  "partially_refunded",
  "refunded",
  "marked_paid",
  "amount_mismatch",
] as const;
export type CustomerOrderPaymentDisplayStateView = (typeof CUSTOMER_ORDER_PAYMENT_DISPLAY_STATE)[number];

export function isCustomerOrderPaymentDisplayState(value: string): value is CustomerOrderPaymentDisplayStateView {
  return (CUSTOMER_ORDER_PAYMENT_DISPLAY_STATE as readonly string[]).includes(value);
}

/** The states in which the collection merchant confirmed money for the order. */
export const PAID_ONLINE_DISPLAY_STATE: readonly CustomerOrderPaymentDisplayStateView[] = [
  "paid_online",
  "partially_refunded",
  "refunded",
];

/**
 * plan-006 FIN-B6 (design §18.28.8, DD6-7): the two states an unpaid online order can be in — a
 * link still working, and a payment that did not complete (the link expired, was cancelled, failed
 * or was never created). Together they are the Orders tab's "Not paid online" chip and the board's
 * column of the same name; apart, each keeps its own label and its own count in the tab's header.
 * In {@link CUSTOMER_ORDER_PAYMENT_DISPLAY_STATE}'s order, which is the header's order.
 */
export const CUSTOMER_ORDER_NOT_PAID_ONLINE_STATE = [
  "awaiting_online_payment",
  "online_payment_not_completed",
] as const satisfies readonly CustomerOrderPaymentDisplayStateView[];
export type CustomerOrderNotPaidOnlineStateView = (typeof CUSTOMER_ORDER_NOT_PAID_ONLINE_STATE)[number];

/** The chip (Orders tab) and the column (the shop board) that hold both of those states. */
export const CUSTOMER_ORDER_NOT_PAID_ONLINE_LABEL = () => (portalText("shell", "not_paid_online_290ba3c5"));

/**
 * OD-P6 (a): waiting for its online payment, so outside the kitchen's "To accept" (the board and
 * the Orders tab show it under "Not paid online"). It enters "To accept" when paid, marked paid or
 * switched to hand-over — each of which changes this state.
 */
export function customerOrderAwaitsPayment(state: CustomerOrderPaymentDisplayStateView): boolean {
  return (CUSTOMER_ORDER_NOT_PAID_ONLINE_STATE as readonly CustomerOrderPaymentDisplayStateView[]).includes(state);
}

/** FR-PAY-10: starting an unpaid online order still warns. A paid or hand-over order does not. */
export function startingWarnsUnpaidOnline(state: CustomerOrderPaymentDisplayStateView): boolean {
  return state === "awaiting_online_payment" || state === "online_payment_not_completed" || state === "amount_mismatch";
}

/**
 * FR-PAY-10: "Mark paid" is unavailable while a link is active — one order cannot be settled
 * twice — and once the order is paid. An expired or cancelled link does not block it.
 */
export function markPaidIsAvailable(state: CustomerOrderPaymentDisplayStateView): boolean {
  return state !== "awaiting_online_payment" && state !== "marked_paid" && !PAID_ONLINE_DISPLAY_STATE.includes(state);
}

/** A paid online order that still has money to give back. */
export function customerOrderIsRefundable(payment: CustomerOrderPaymentView): boolean {
  return (
    (payment.state === "paid_online" || payment.state === "partially_refunded") &&
    payment.refundable.amountMinor > 0
  );
}

/** What each state is called on a tag, in a sentence and in the CSV (FR-PAY-10: each distinct). */
export const CUSTOMER_ORDER_PAYMENT_STATE_LABEL: Readonly<Record<CustomerOrderPaymentDisplayStateView, string>> = validatedDisplayView({
  get not_online() { return portalText("shell", "paid_on_hand_over_6542377e"); },
  get awaiting_online_payment() { return portalText("shell", "awaiting_online_payment_157cf5dd"); },
  get online_payment_not_completed() { return portalText("shell", "online_payment_not_completed_3271c9bb"); },
  get paid_online() { return portalText("shell", "paid_online_c95e12bc"); },
  get partially_refunded() { return portalText("shell", "partially_refunded_c612c0a4"); },
  get refunded() { return portalText("shell", "refunded_d6fbc7a8"); },
  get marked_paid() { return portalText("shell", "marked_paid_31242153"); },
  get amount_mismatch() { return portalText("shell", "amount_mismatch_cb5f9fc0"); },
});

/**
 * One clause of the Orders tab's header (§18.28.8): the count, then the state's own label in
 * sentence case — "1 awaiting online payment", "2 online payment not completed". The label is the
 * one a row of that state carries, so the header and the rows cannot use different words.
 */
export function customerOrderPaymentStateCountText(state: CustomerOrderPaymentDisplayStateView, count: number): string {
  const label = CUSTOMER_ORDER_PAYMENT_STATE_LABEL[state];
  return `${String(count)} ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

/** The tag's semantic role: money on its way is caution, money that did not come is critical. */
export const CUSTOMER_ORDER_PAYMENT_STATE_TONE: Readonly<
  Record<CustomerOrderPaymentDisplayStateView, "positive" | "caution" | "critical" | "muted">
> = {
  not_online: "muted",
  awaiting_online_payment: "caution",
  online_payment_not_completed: "critical",
  paid_online: "positive",
  partially_refunded: "muted",
  refunded: "muted",
  marked_paid: "positive",
  amount_mismatch: "critical",
};

// ---------------------------------------------------------------------------------------------
// Who may do what (OD-P5). The route's own roles, named beside the route, as
// `shell/member-capability.ts` does for pages the capability matrix has no tick for.
// ---------------------------------------------------------------------------------------------

/** `POST /v1/orders/{ref}/refunds` — `refundCustomerOrder` (IC-52a, OD-P5). */
export const CUSTOMER_ORDER_REFUND_ROLE: readonly MemberRole[] = ["owner", "admin"];
/** Resend, cancel the link, switch method — `resendCustomerOrderPaymentLink` and its siblings. */
export const CUSTOMER_ORDER_PAYMENT_MANAGE_ROLE: readonly MemberRole[] = ["owner", "admin", "member"];

export function mayRefundCustomerOrder(role: WorkspaceRoleView | null): boolean {
  return role !== null && CUSTOMER_ORDER_REFUND_ROLE.includes(role);
}

export function mayManageCustomerOrderPayment(role: WorkspaceRoleView | null): boolean {
  return role !== null && CUSTOMER_ORDER_PAYMENT_MANAGE_ROLE.includes(role);
}

// ---------------------------------------------------------------------------------------------
// The detail's `payment` block
// ---------------------------------------------------------------------------------------------

export interface CustomerOrderPaymentSmsView {
  readonly state: SmsMessageState;
  readonly destinationLastThree: string;
  /** "Text message to •••187 · delivered · Today 19:44". */
  readonly text: string;
}

export interface CustomerOrderPaymentTimelineEntryView {
  readonly kind: CustomerOrderPaymentEventKind;
  readonly atText: string;
  /** The event in words — "Payment link sent" — never a bare time. */
  readonly text: string;
}

export interface CustomerOrderPaymentView {
  readonly state: CustomerOrderPaymentDisplayStateView;
  /** When the link stops working; set while `awaiting_online_payment`. */
  readonly expiresAt: Date | null;
  /** "19:42" (today) or "25 Sept, 19:42" — the expiry as the shop reads it. */
  readonly expiresAtText: string | null;
  readonly paidAtText: string | null;
  readonly paidAmount: Money | null;
  readonly refunded: Money;
  /** What a refund may still return: the paid amount less every refund not failed. */
  readonly refundable: Money;
  /** The placeholder page: shown as a test, never as money. */
  readonly isTest: boolean;
  readonly providerMode: ProviderMode;
  readonly linkActive: boolean;
  readonly sms: CustomerOrderPaymentSmsView | null;
  readonly timeline: readonly CustomerOrderPaymentTimelineEntryView[];
}

const CLOCK = () => (new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { hour: "2-digit", minute: "2-digit", hour12: false }));
const DAY_AND_CLOCK = () => (new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
}));

/** "19:42" when it is today, "25 Sept, 19:42" otherwise. */
export function paymentMomentText(at: Date, now: Date): string {
  const sameDay =
    at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate();
  return sameDay ? CLOCK().format(at) : DAY_AND_CLOCK().format(at);
}

const SMS_STATE_TEXT: Readonly<Record<SmsMessageState, string>> = validatedDisplayView({
  get created() { return portalText("shell", "being_sent_05c80833"); },
  get sending() { return portalText("shell", "being_sent_05c80833"); },
  accepted: "sent",
  sent: "sent",
  delivered: "delivered",
  get failed() { return portalText("shell", "not_delivered_39c14fb7"); },
  get rejected() { return portalText("shell", "refused_by_the_network_aac8b63f"); },
  get expired() { return portalText("shell", "not_delivered_in_time_8a9046fb"); },
  get unknown() { return portalText("shell", "delivery_not_confirmed_0b2cf861"); },
});

const TIMELINE_TEXT: Readonly<Record<CustomerOrderPaymentEventKind, string>> = validatedDisplayView({
  get link_created() { return portalText("shell", "payment_link_created_6d386b1d"); },
  get link_delivered() { return portalText("shell", "payment_link_sent_6e97e340"); },
  get link_delivery_failed() { return portalText("shell", "payment_link_could_not_be_sent_6f47597b"); },
  get link_resent() { return portalText("shell", "payment_link_sent_again_a2b7b881"); },
  get link_cancelled() { return portalText("shell", "payment_link_cancelled_cad0af88"); },
  get paid() { return portalText("shell", "paid_online_c95e12bc"); },
  get paid_after_method_change() { return portalText("shell", "paid_online_after_the_method_was_changed_f41c24f9"); },
  get amount_mismatch() { return portalText("shell", "paid_a_different_amount_wetalk_is_checking_it_07efb59d"); },
  get expired() { return portalText("shell", "payment_link_expired_63b885c4"); },
  get not_created() { return portalText("shell", "payment_link_could_not_be_created_54c0b5c0"); },
  get payment_method_changed() { return portalText("shell", "switched_to_payment_on_hand_over_6893d99a"); },
  get marked_paid_elsewhere() { return portalText("shell", "marked_paid_31242153"); },
  get refund_requested() { return portalText("shell", "refund_requested_9a41b1f4"); },
  get refund_succeeded() { return portalText("shell", "refunded_d6fbc7a8"); },
  get refund_failed() { return portalText("shell", "refund_failed_901ba670"); },
  get late_payment_refund() { return portalText("shell", "paid_after_cancelling_refunded_automatically_3fe25014"); },
  get dispute_opened() { return portalText("shell", "the_payer_disputed_the_payment_88eb43bb"); },
  get dispute_won() { return portalText("shell", "dispute_decided_for_the_shop_4ee89165"); },
  get dispute_lost() { return portalText("shell", "dispute_decided_for_the_payer_24e09560"); },
  get sms_accepted() { return portalText("shell", "text_message_accepted_by_the_network_263f3a2c"); },
  get sms_delivered() { return portalText("shell", "text_message_delivered_22602782"); },
  get sms_failed() { return portalText("shell", "text_message_not_delivered_35e73a7e"); },
  get sms_unknown() { return portalText("shell", "text_message_delivery_not_confirmed_27e5c7e1"); },
});

const ACTOR_TEXT: Readonly<Record<string, string>> = validatedDisplayView({
  get person() { return portalText("shell", "from_the_console_5c0396d2"); },
  get operator() { return portalText("shell", "by_wetalk_support_960bb16a"); },
  get payer() { return portalText("shell", "by_the_caller_569cd4e4"); },
});

function oneOf<const T extends readonly string[]>(allowed: T, raw: unknown, at: string, expected: string): T[number] {
  const value = readText(raw, at);
  const found = allowed.find((one) => one === value);
  if (found === undefined) throw new MalformedResponseError(at, expected, value);
  return found;
}

function nullableInstant(raw: unknown, at: string): Date | null {
  if (raw === null || raw === undefined) return null;
  const when = new Date(readText(raw, at));
  if (Number.isNaN(when.getTime())) throw new MalformedResponseError(at, "an RFC 3339 timestamp", raw);
  return when;
}

function minorOf(raw: unknown, at: string, currency: CurrencyCode): Money {
  return money(readWholeNumber(raw, at), currency);
}

/**
 * The detail's `payment` (IC-52a): `null` for an order no payment was ever attempted for and that
 * was not marked paid — the order's own row facts then say everything.
 */
export function customerOrderPaymentOfWire(raw: unknown, at: string, now: Date): CustomerOrderPaymentView | null {
  if (raw === null || raw === undefined) return null;
  const payment = readRecord(raw, at);
  const currencyText = readText(payment["currency"], `${at}.currency`);
  if (currencyText !== "EUR") {
    throw new MalformedResponseError(`${at}.currency`, "EUR, which is what this console renders", currencyText);
  }
  const currency: CurrencyCode = currencyText;
  const expiresAt = nullableInstant(payment["expires_at"], `${at}.expires_at`);
  const paidAt = nullableInstant(payment["paid_at"], `${at}.paid_at`);
  const paidMinor = payment["paid_amount_minor"];
  const smsRaw = payment["sms"];

  return validatedDisplayView({
    state: oneOf(CUSTOMER_ORDER_PAYMENT_DISPLAY_STATE, payment["state"], `${at}.state`, portalText("shell", "a_documented_payment_state_a9ea48ff")),
    expiresAt,
    get expiresAtText() { return expiresAt === null ? null : paymentMomentText(expiresAt, now); },
    get paidAtText() { return paidAt === null ? null : paymentMomentText(paidAt, now); },
    paidAmount: paidMinor === null || paidMinor === undefined ? null : minorOf(paidMinor, `${at}.paid_amount_minor`, currency),
    refunded: minorOf(payment["refunded_minor"], `${at}.refunded_minor`, currency),
    refundable: minorOf(payment["refundable_minor"], `${at}.refundable_minor`, currency),
    isTest: readBoolean(payment["is_test"], `${at}.is_test`),
    providerMode: oneOf(PROVIDER_MODE, payment["provider_mode"], `${at}.provider_mode`, portalText("shell", "sandbox_or_live_9e0b5526")),
    linkActive: readBoolean(payment["link_active"], `${at}.link_active`),
    sms: smsRaw === null || smsRaw === undefined ? null : smsOfWire(smsRaw, `${at}.sms`, now),
    timeline: readArray(payment["timeline"], `${at}.timeline`).map((one, index) =>
      timelineEntryOfWire(one, `${at}.timeline[${index}]`, now),
    ),
  });
}

function smsOfWire(raw: unknown, at: string, now: Date): CustomerOrderPaymentSmsView {
  const sms = readRecord(raw, at);
  const state = oneOf(SMS_MESSAGE_STATE, sms["state"], `${at}.state`, portalText("shell", "a_documented_sms_state_9b5a544a"));
  const destinationLastThree = readText(sms["destination_last_three"], `${at}.destination_last_three`);
  const when = nullableInstant(sms["at"], `${at}.at`);
  return {
    state,
    destinationLastThree,
    get text() { return portalText("shell", "text_message_to_value_valuevalue_d2b2fe45", { v0: String(destinationLastThree), v1: String(SMS_STATE_TEXT[state]), v2: String(when === null ? "" : ` · ${paymentMomentText(when, now)}`) }); },
  };
}

function timelineEntryOfWire(raw: unknown, at: string, now: Date): CustomerOrderPaymentTimelineEntryView {
  const entry = readRecord(raw, at);
  const kind = oneOf(CUSTOMER_ORDER_PAYMENT_EVENT_KIND, entry["kind"], `${at}.kind`, portalText("shell", "a_documented_payment_event_f8348546"));
  const occurredAt = nullableInstant(entry["occurred_at"], `${at}.occurred_at`);
  if (occurredAt === null) throw new MalformedResponseError(`${at}.occurred_at`, "an RFC 3339 timestamp", null);
  const actorKind = typeof entry["actor_kind"] === "string" ? entry["actor_kind"] : "";
  const detail = typeof entry["detail"] === "string" && entry["detail"] !== "" ? `: ${entry["detail"]}` : "";
  return validatedDisplayView({
    kind,
    get atText() { return paymentMomentText(occurredAt, now); },
    text: `${TIMELINE_TEXT[kind]}${detail}${ACTOR_TEXT[actorKind] ?? ""}`,
  });
}

/**
 * The sub-line under an order's total, from its display state (FR-PAY-10: each state distinct).
 * `notOnlineText` is what the order's method says today ("Cash", "Card on delivery", "Unpaid").
 */
export function customerOrderPaymentLineOf(input: {
  readonly state: CustomerOrderPaymentDisplayStateView;
  readonly isTest: boolean;
  readonly paymentMethod: string | null;
  readonly notOnlineText: string;
}): string {
  const trialMark = input.isTest ? portalText("shell", "test_ecd6e95c") : "";
  switch (input.state) {
    case "not_online":
      return input.notOnlineText;
    case "marked_paid":
      // A cash or card order that was paid reads as it always did; an online one says it was
      // settled another way, which is what "marked paid" means for it.
      return input.paymentMethod === "online" ? portalText("shell", "marked_paid_settled_another_way_6c397871") : portalText("shell", "paid_dc9d4584");
    case "awaiting_online_payment":
      return portalText("shell", "awaiting_online_paymentvalue_7f06ee2c", { v0: String(trialMark) });
    case "online_payment_not_completed":
      return portalText("shell", "online_payment_not_completedvalue_ed66734a", { v0: String(trialMark) });
    case "paid_online":
      return portalText("shell", "paid_onlinevalue_9e7e4090", { v0: String(trialMark) });
    case "partially_refunded":
      return portalText("shell", "paid_online_partly_refundedvalue_192f0258", { v0: String(trialMark) });
    case "refunded":
      return portalText("shell", "refundedvalue_0fa0828e", { v0: String(trialMark) });
    case "amount_mismatch":
      return portalText("shell", "paid_a_different_amount_wetalk_is_checking_it_1e01f431");
  }
}

// ---------------------------------------------------------------------------------------------
// The acts (IC-52a) and the shop stream
// ---------------------------------------------------------------------------------------------

/** The two methods an online order can be switched to (`setCustomerOrderPaymentMethod`). */
export const HAND_OVER_PAYMENT_METHOD = ["cash", "card_on_delivery"] as const;
export type HandOverPaymentMethodView = (typeof HAND_OVER_PAYMENT_METHOD)[number];

export const HAND_OVER_PAYMENT_METHOD_LABEL: Readonly<Record<HandOverPaymentMethodView, string>> = validatedDisplayView({
  get cash() { return portalText("shell", "cash_on_hand_over_cdc27df0"); },
  get card_on_delivery() { return portalText("shell", "card_on_hand_over_9e8dc706"); },
});

export interface CustomerOrderRefundRequestView {
  /** `null` refunds everything still refundable (the API's own rule). */
  readonly amountMinor: number | null;
  readonly reason: string;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /**
     * The same link again while it is valid (by SMS for a phone order, within the caps); a new
     * link after expiry, failure or cancel. The worker executes it; the screen re-reads.
     */
    resendCustomerOrderPaymentLink(customerOrderReference: string): Promise<void>;
    /** Cancels an open link (a `paid` answer settles the attempt instead). */
    cancelCustomerOrderPaymentLink(customerOrderReference: string): Promise<void>;
    /** Owner/Admin (OD-P5): full (`amountMinor: null`) or partial, with a reason. */
    refundCustomerOrder(customerOrderReference: string, request: CustomerOrderRefundRequestView): Promise<void>;
    /** Switches an online order to payment on hand-over, cancelling an open link. */
    setCustomerOrderPaymentMethod(
      customerOrderReference: string,
      paymentMethod: HandOverPaymentMethodView,
    ): Promise<void>;
    /**
     * The shop's authenticated order stream (`streamShopCustomerOrder`). Every payment change
     * publishes the existing `customer_order` event there (DD5-33), so the screens re-read on it.
     */
    streamShopCustomerOrder(shopId: string, onEvent: StreamListener): StreamSubscription;
  }
}

export type CustomerOrderPaymentGatewaySlice = Pick<
  ConsoleGateway,
  | "resendCustomerOrderPaymentLink"
  | "cancelCustomerOrderPaymentLink"
  | "refundCustomerOrder"
  | "setCustomerOrderPaymentMethod"
  | "streamShopCustomerOrder"
>;

export const customerOrderPaymentGap: readonly GatewayGap[] = [];

/** The events on the shop stream that mean "re-read the order book" (DD5-33). */
export const CUSTOMER_ORDER_REREAD_EVENT: readonly string[] = [
  "customer_order",
  "customer_order_payment",
  "snapshot",
  "resync",
];

/** One IC-52a operation this slice calls, and the unit that adds it to the spec. */
const OPERATION = validatedDisplayView({
  get resend() { return validatedDisplayView({ method: "resendCustomerOrderPaymentLink", wants: "resendCustomerOrderPaymentLink", get owner() { return "OP-35"; } }); },
  get cancel() { return validatedDisplayView({ method: "cancelCustomerOrderPaymentLink", wants: "cancelCustomerOrderPaymentLink", get owner() { return "OP-35"; } }); },
  get refund() { return validatedDisplayView({ method: "refundCustomerOrder", wants: "refundCustomerOrder", get owner() { return "OP-35"; } }); },
  get method() { return validatedDisplayView({ method: "setCustomerOrderPaymentMethod", wants: "setCustomerOrderPaymentMethod", get owner() { return "OP-35"; } }); },
}) satisfies Readonly<Record<string, GatewayGap>>;

type GeneratedOperation = (request: {
  readonly path: Readonly<Record<string, string>>;
  readonly body: unknown;
}) => Promise<ApiResult>;

/**
 * The generated client's method for a DOCUMENTED operation, or {@link UndocumentedOperationError}.
 *
 * Both halves are required: the operationId in the assembled spec (`API_OPERATION`), and the
 * method on the generated client. Neither is a path string, so nothing here can reach a route the
 * spec does not carry. Synchronous, like `refuseFrom`: an operation that was never built is a
 * programming fault, not a request that failed.
 */
export function documentedOperationOf(client: WeTalkApiClient, gap: GatewayGap): GeneratedOperation {
  const documented = API_OPERATION.some((one) => one.operationId === gap.wants);
  const method: unknown = Reflect.get(client, gap.wants);
  if (!documented || typeof method !== "function") throw new UndocumentedOperationError(gap);
  return (request) => Promise.resolve(Reflect.apply(method, client, [request]) as Promise<ApiResult>);
}

export function customerOrderPaymentApiSlice(
  client: WeTalkApiClient,
  stream: (
    operationId: string,
    pathParameter: Readonly<Record<string, string>>,
    onEvent: StreamListener,
  ) => StreamSubscription,
): CustomerOrderPaymentGatewaySlice {
  const sendTo = (gap: GatewayGap, reference: string, body: unknown): Promise<void> => {
    const operation = documentedOperationOf(client, gap);
    return operation({ path: { customer_order_reference: reference }, body }).then((result) => {
      assertOk(result);
    });
  };
  return {
    resendCustomerOrderPaymentLink: (reference) => sendTo(OPERATION.resend, reference, { action: "resend" }),
    cancelCustomerOrderPaymentLink: (reference) => sendTo(OPERATION.cancel, reference, {}),
    refundCustomerOrder: (reference, request) =>
      sendTo(OPERATION.refund, reference, { amount_minor: request.amountMinor, reason: request.reason }),
    setCustomerOrderPaymentMethod: (reference, paymentMethod) =>
      sendTo(OPERATION.method, reference, { payment_method: paymentMethod }),
    streamShopCustomerOrder: (shopId, onEvent) => stream("streamShopCustomerOrder", { shop_id: shopId }, onEvent),
  };
}
